import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LogoutButton from "../LogoutButton.jsx";
import { supabase } from "../../supabaseClient.js";
import logoNeoAI from "../../assets/Asset 4.svg";
import * as LucideIcons from "lucide-react";
import CacheService from "../../services/CacheService.js";
import { MEMORY_CACHE } from "../../services/GlobalCache.js";

const ICON_BUCKET = "assets";
const CACHE_KEY_TOOLS = 'sidebar_tools';

const DynamicIcon = ({ name, className }) => {
  const Icon = LucideIcons[name] || LucideIcons.Box;
  return <Icon className={className} />;
};

const DashboardSidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeModelId, setActiveModelId] = useState(location.state?.selectedModelId);

  // Sync activeModelId (Event + Location)
  useEffect(() => {
    setActiveModelId(location.state?.selectedModelId);
  }, [location.state?.selectedModelId]);

  useEffect(() => {
    const handleModelChange = (e) => {
      // console.log("Sidebar Model Event:", e.detail.modelId);
      setActiveModelId(e.detail.modelId);
    };
    window.addEventListener('neo-model-change', handleModelChange);
    return () => window.removeEventListener('neo-model-change', handleModelChange);
  }, []);

  // Desktop collapse state - saved to localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('neo_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  // Toggle collapse and save to localStorage
  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('neo_sidebar_collapsed', String(newValue));
      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('sidebar-collapse-change', { detail: { collapsed: newValue } }));
      return newValue;
    });
  };

  // Initialize userTools from CacheService to prevent pop-in delay
  const [userTools, setUserTools] = useState(() => {
    const cached = CacheService.get(CACHE_KEY_TOOLS);
    return cached || [];
  });

  // Fixed Sidebar items
  const fixedItems = [
    { key: "home", label: t('sidebar.home'), route_path: "/dashboard", icon_url: "LayoutDashboard" },
    { key: "topup", label: t('sidebar.topup'), route_path: "/dashboard/topup", icon_url: "CreditCard" },
    { key: "history", label: t('sidebar.history'), route_path: "/dashboard/riwayat-generate", icon_url: "History" },
    { key: "affiliator", label: t('sidebar.affiliator'), route_path: "/dashboard/affiliator", icon_url: "Users" },
  ];


  // Store user ID for realtime filter
  const [currentUserId, setCurrentUserId] = useState(null);

  // Fetch user tools function (extracted for reuse with useCallback)
  const fetchUserTools = useCallback(async (userId) => {
    if (!userId) return;

    const { data: uFeats } = await supabase
      .from("user_features")
      .select("feature_code")
      .eq("user_id", userId);

    if (uFeats && uFeats.length > 0) {
      const codes = uFeats.map((r) => r.feature_code);
      const { data: apps } = await supabase
        .from("app_features")
        .select("*")
        .in("slug", codes) // Match slug
        .eq("is_active", true)
        .order("sort_order");

      if (apps) {
        // Only update state if data actually changed (deep comparison optimization could be added here, but simple set is fine)
        setUserTools(apps);
        // Cache the result to eliminate future loading delay (Expiry 60 mins)
        CacheService.set(CACHE_KEY_TOOLS, apps, 60);
      }
    } else {
      // No tools assigned
      setUserTools([]);
      CacheService.set(CACHE_KEY_TOOLS, [], 60);
    }
  }, []);

  // Fetch and Cache Models (Reusable for Lazy Load & Realtime)
  const fetchModelsForSlug = useCallback(async (slug) => {
    if (!slug) return;

    // RESOLVE ID: Try to find feature_id from slug to ensure robust matching
    const tool = userTools.find(t => t.slug === slug);
    const featureId = tool?.id;

    console.log(`Sidebar Fetch: slug=${slug} foundID=${featureId}`);

    try {
      let query = supabase
        .from('ai_models')
        // Use explicit fields to match GenericAiPage and avoid "ambiguous column" or bad request errors with *
        .select(`
            id,name,key,price_credits,description,created_at,
            configuration_options,status,is_active,video_url,label,
            feature_code, feature_id,
            ai_model_providers(id,name,icon_url)
        `);

      // Prefer querying by ID if available, otherwise fallback to slug
      if (featureId) {
        query = query.eq('feature_id', featureId);
      } else {
        query = query.eq('feature_code', slug);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Supabase Error Sidebar:", error);
        return;
      }

      if (data) {
        // Filter active/dev models in memory
        const validModels = data.filter(m => ['active', 'development'].includes(m.status));

        // Sort in memory (Matches GenericAiPage logic: sort_order is inside configuration_options JSON)
        validModels.sort((a, b) => {
          const orderA = a.configuration_options?.sort_order ?? 9999;
          const orderB = b.configuration_options?.sort_order ?? 9999;
          if (orderA !== orderB) return orderA - orderB;
          return new Date(b.created_at) - new Date(a.created_at);
        });

        // NON-DESTRUCTIVE UPDATE: Merge with existing cache
        const existing = MEMORY_CACHE[slug] || CacheService.get(`__TOOL_${slug}__`) || {};
        const payload = { ...existing, models: validModels };

        if (!payload.fields) payload.fields = [];

        MEMORY_CACHE[slug] = payload;
        CacheService.set(`__TOOL_${slug}__`, payload, 60);

        console.log(`✅ Sidebar: Models updated for ${slug} (${validModels.length} items)`);

        setExpandedItems(prev => ({ ...prev }));
      }
    } catch (err) {
      console.error("Error refreshing sidebar models:", err);
    }
  }, [userTools]);

  // Fetch Models for Sidebar Dropdown (Lazy Load)
  const checkOrFetchModels = (slug) => {
    if (!slug) return;

    // 1. Check Memory Cache
    if (MEMORY_CACHE[slug]?.models) return;

    // 2. Check Disk Cache
    const cached = CacheService.get(`__TOOL_${slug}__`);
    if (cached?.models) {
      MEMORY_CACHE[slug] = cached;
      return; // Already have data
    }

    // 3. Fetch from DB
    fetchModelsForSlug(slug);
  };

  // Initial fetch
  useEffect(() => {
    const initFetch = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        // Fetch immediately in background to update cache if stale
        fetchUserTools(user.id);
      }
    };
    initFetch();
  }, []);

  // Keep userTools in a ref for access inside useEffect/Realtime without dependency loop
  const userToolsRef = React.useRef(userTools);
  useEffect(() => {
    userToolsRef.current = userTools;
  }, [userTools]);

  // Realtime subscription for user_features and app_features
  useEffect(() => {
    if (!currentUserId) return;

    // Channel for realtime updates
    const channel = supabase.channel('sidebar_tools_updates')
      // Listen for changes in user_features (admin adds/removes tool access)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_features', filter: `user_id=eq.${currentUserId}` },
        (payload) => {
          console.log('🔄 Sidebar: user_features changed', payload);
          fetchUserTools(currentUserId);
        }
      )
      // Listen for changes in app_features (admin modifies tool config)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_features' },
        (payload) => {
          console.log('🔄 Sidebar: app_features changed', payload);
          fetchUserTools(currentUserId);
        }
      )
      // Listen for changes in ai_models (Realtime Dropdown Update)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ai_models' },
        async (payload) => {
          // robust lookup: payload might validly contain feature_code OR feature_id
          const record = payload.new || payload.old;
          if (!record) return;

          let slug = record.feature_code;

          // Fallback: If feature_code hidden (e.g. partial update), allow lookup by ID
          if (!slug && record.feature_id) {
            const found = userToolsRef.current.find(t => t.id === record.feature_id);
            if (found) slug = found.slug;
          }

          // Fallback 2 (CRITICAL FOR DELETE): On Delete, we often only get the ID.
          // We must reverse-search our Memory Cache OR Disk Cache to find which slug contained this model.
          if (!slug && payload.eventType === 'DELETE' && payload.old?.id) {
            const targetId = payload.old.id;
            // Iterate ALL possible tools the user has to be thorough
            const allTools = userToolsRef.current || [];

            for (const tool of allTools) {
              const s = tool.slug;
              // Check RAM first
              let cacheData = MEMORY_CACHE[s];
              // If not in RAM, Check Disk
              if (!cacheData) {
                cacheData = CacheService.get(`__TOOL_${s}__`);
              }

              // Use loose equality (==) to catch string vs number ID mismatches
              if (cacheData?.models && cacheData.models.some(m => m.id == targetId)) {
                slug = s;
                console.log(`🗑️ Sidebar: Detect DELETE for model ${targetId}, found in slug: ${slug}`);
                break;
              }
            }
          }

          if (slug) {
            console.log('🔄 Sidebar: Realtime trigger for:', slug);
            // Short delay to ensure DB commit is visible
            setTimeout(() => fetchModelsForSlug(slug), 500);
          }
        }
      )
      .subscribe();

    // Cleanup on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, fetchUserTools, fetchModelsForSlug]);

  // Listen for Recreate Request (Event Bus Pattern to sync Sidebar)
  useEffect(() => {
    const handleRecreateRequest = async (e) => {
      const { featureSlug, modelKey, modelName } = e.detail;
      console.log("Sidebar handle recreate:", featureSlug, modelKey);

      const tools = userToolsRef.current || [];
      const tool = tools.find(t => t.slug === featureSlug || t.feature_code === featureSlug);

      if (tool) {
        // A. Expand Sidebar Group if collapsed
        setExpandedItems(prev => {
          if (prev[tool.slug]) return prev;
          return { ...prev, [tool.slug]: true };
        });

        // B. Ensure Models are Loaded
        let models = MEMORY_CACHE[tool.slug]?.models;
        if (!models) {
          // Force fetch (we await the internal update)
          await fetchModelsForSlug(tool.slug);
          models = MEMORY_CACHE[tool.slug]?.models;
        }

        // C. Resolve Target ID
        let targetId = null;
        if (models) {
          const foundKey = models.find(m => m.key === modelKey);
          if (foundKey) targetId = foundKey.id;
          else if (modelName) {
            // Fuzzy match
            const norm = modelName.trim().toLowerCase();
            const foundName = models.find(m =>
              (m.name || "").toLowerCase() === norm ||
              (m.label || "").toLowerCase() === norm
            );
            if (foundName) targetId = foundName.id;
          }
        }

        // D. Navigate
        navigate(`/dashboard/feature/${tool.slug}`, {
          state: {
            selectedModelId: targetId,
            restoreModelKey: modelKey,
            restoreModelName: modelName
          }
        });
      } else {
        // Fallback if tool not found in Sidebar (e.g. invalid slug), direct navigate
        navigate(`/dashboard/feature/${featureSlug}`, {
          state: { restoreModelKey: modelKey, restoreModelName: modelName }
        });
      }
    };

    window.addEventListener('neo-request-recreate', handleRecreateRequest);
    return () => window.removeEventListener('neo-request-recreate', handleRecreateRequest);
  }, [fetchModelsForSlug, navigate]);

  const handleNavigate = (path) => {
    if (!path) return;
    navigate(path);
    setIsOpen(false);
  };

  const getIconUrl = (raw) => {
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    try {
      const { data } = supabase.storage.from(ICON_BUCKET).getPublicUrl(raw);
      return data?.publicUrl || null;
    } catch {
      return null;
    }
  };

  // event dari navbar (burger)
  useEffect(() => {
    const handler = () => setIsOpen((prev) => !prev);
    window.addEventListener("toggle-dashboard-sidebar", handler);
    return () =>
      window.removeEventListener("toggle-dashboard-sidebar", handler);
  }, []);

  // Auto-scroll to active item
  const activeItemRef = React.useRef(null);

  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [location.pathname, userTools]);

  // Determine effective collapse state for rendering
  // On mobile/when open, we force expanded view (effectiveCollapsed = false) because mobile menu should be full width with text.
  // On desktop (isOpen is usually false unless resized), we respect isCollapsed preference.
  // Exception: If on desktop and somehow isOpen is true (resize edge case), we force expand to avoid layout mismatch.
  const effectiveCollapsed = isCollapsed && !isOpen;

  // State for expanded sidebar items (Dropwon)
  const [expandedItems, setExpandedItems] = useState({});



  const toggleItem = (slug, e) => {
    e.preventDefault();
    e.stopPropagation();

    setExpandedItems(prev => {
      const isExpanding = !prev[slug];
      if (isExpanding) {
        // OPTIMIZED: Use Cache First Strategy.
        // If data exists in MEMORY_CACHE or localStorage, use it immediately (0ms DB hit).
        // Realtime updates are handled separately by the Supabase Listener in the background.
        checkOrFetchModels(slug);
      }
      return { ...prev, [slug]: isExpanding };
    });
  };

  const renderItem = (item, collapsed = false) => {
    // Determine path: fixed uses route_path. Tools use /dashboard/feature/:slug
    const path = item.route_path || `/dashboard/feature/${item.slug}`;
    const isActive = path === '/dashboard'
      ? location.pathname === path
      : location.pathname.startsWith(path);
    const labelText = item.label || item.name || "Menu";
    const rawIcon = item.icon_url || item.lucide_icon || item.icon;

    const isImage = rawIcon && (rawIcon.includes('/') || rawIcon.includes('.'));
    const iconUrl = isImage ? getIconUrl(rawIcon) : null;
    const isLucide = rawIcon && !isImage;

    // Check for cached models (for dropdown)
    // We check MEMORY_CACHE mostly because it's sync and fast
    const cachedData = item.slug ? (MEMORY_CACHE[item.slug] || CacheService.get(`__TOOL_${item.slug}__`)) : null;
    const models = cachedData?.models?.filter(m => m.status === 'active' || m.status === 'development') || [];

    // Logic update: If we have models, show dropdown. 
    // IF we don't have models yet (not fetched), but it's a tool (has slug), we assume it MIGHT have models 
    // so we show the chevron to allow the user to click and trigger the fetch (checkOrFetchModels).
    // If after fetch it's still 0, it will hide.
    const hasDropdown = models.length > 0 || (item.slug && !cachedData);

    const isExpanded = expandedItems[item.slug];

    // Collapsed Sidebar Mode: Icon only with tooltip (No Dropdown visual, just link)
    if (collapsed) {
      return (
        <div
          className="relative w-full flex justify-center"
          key={item.id || item.key || path}
          ref={isActive ? activeItemRef : null}
        >
          <button
            type="button"
            onClick={() => handleNavigate(path)}
            title={labelText}
            className={`
              w-10 h-10 flex items-center justify-center rounded-xl relative
              transition-all duration-200
              ${isActive
                ? "bg-sky-100 text-sky-700 ring-2 ring-sky-200"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              }
            `}
          >
            {isImage && iconUrl ? (
              <img
                src={iconUrl}
                alt=""
                className="w-5 h-5 rounded object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : isLucide ? (
              <DynamicIcon name={rawIcon} className="w-5 h-5" />
            ) : (
              <span className="text-xs font-bold">{labelText.charAt(0)}</span>
            )}
          </button>
        </div>
      );
    }

    // Expanded Sidebar Mode: Full item with potential Dropdown
    return (
      <div
        className="relative w-full group/item"
        key={item.id || item.key || path}
        ref={isActive ? activeItemRef : null}
      >
        <div className={`relative mx-3 ${hasDropdown && isExpanded ? "sticky top-0 z-30 bg-sky-50" : ""}`}>
          <button
            type="button"
            onClick={() => handleNavigate(path)}
            className={`
                w-full flex items-center gap-3
                py-2 px-3 relative rounded-lg
                text-[13px] font-medium font-sans
                overflow-hidden transition-all duration-200
                ${isActive
                ? "bg-sky-50/80 text-sky-700 font-semibold"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-900 border border-transparent"
              }
            `}
          >
            <div className="relative flex-shrink-0 z-10 transition-transform duration-200 group-hover/item:scale-105">
              {isImage && iconUrl ? (
                <img
                  src={iconUrl}
                  alt=""
                  className="w-5 h-5 rounded object-contain mix-blend-multiply dark:mix-blend-normal"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : isLucide ? (
                <div className={`transition-colors ${isActive ? "text-sky-600" : "text-slate-400 group-hover/item:text-slate-600"}`}>
                  <DynamicIcon name={rawIcon} className="w-5 h-5" />
                </div>
              ) : (
                <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold transition-all ${isActive ? "bg-sky-600 text-white shadow-sm" : "bg-slate-100 text-slate-500 group-hover/item:bg-slate-200"}`}>
                  {labelText.charAt(0)}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 text-left z-10 grid grid-cols-[1fr_auto] items-center gap-2">
              <div className="flex flex-col justify-center min-w-0">
                <span className={`block truncate leading-tight ${item.status_badge ? 'text-[13px] -mb-0.5' : 'text-[13px]'}`}>
                  {labelText}
                </span>
                {item.status_badge && (
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap w-fit mt-1 inline-block ${item.status_badge === "New"
                    ? "bg-emerald-50 text-emerald-600"
                    : item.status_badge === "Populer"
                      ? "bg-orange-50 text-orange-600"
                      : "bg-blue-50 text-blue-600"
                    }`}>
                    {item.status_badge}
                  </span>
                )}
              </div>

              {/* Dropdown Chevron */}
              {hasDropdown && (
                <div
                  onClick={(e) => toggleItem(item.slug, e)}
                  className={`p-1 -mr-1 rounded-md hover:bg-slate-200/50 text-slate-400 hover:text-slate-600 transition-all cursor-pointer z-50 ${isExpanded ? 'bg-slate-100 text-slate-600 rotate-180' : ''}`}
                >
                  <LucideIcons.ChevronDown size={14} className="stroke-[2.5]" />
                </div>
              )}
            </div>
          </button>
        </div>

        {/* Sub-menu (Models) - Tree View Style */}
        <div className={`grid transition-all duration-300 ease-in-out ${hasDropdown && isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
          <div className="overflow-hidden">
            <div className="pl-[2.85rem] pr-3 my-1 relative">
              {/* Connection Line */}
              <div className="absolute left-[1.65rem] top-0 bottom-2 w-px bg-slate-200" />

              <div className="flex flex-col gap-0.5">
                {models.map(model => {
                  const isModelActive = isActive && activeModelId == model.id;

                  // Helper to format name if it contains JSON (e.g. Badge Data)
                  // Helper to format name if it contains JSON (e.g. Badge Data)
                  let displayName = model.name;
                  if (typeof displayName === 'string') {
                    // Check for JSON-like pattern
                    if (displayName.trim().startsWith('{') || displayName.includes('"text":')) {
                      try {
                        const parsed = JSON.parse(displayName);
                        // Prioritize 'name' if exists, else 'text' (common in badge), else 'label'
                        displayName = parsed.name || parsed.text || parsed.label || displayName;
                      } catch (e) {
                        // Fallback: Regex extract "text":"Value"
                        const match = displayName.match(/"text":"([^"]+)"/);
                        if (match) displayName = match[1];
                      }
                    }
                  }

                  return (
                    <button
                      key={model.id}
                      onClick={() => {
                        navigate(`/dashboard/feature/${item.slug}`, { state: { selectedModelId: model.id } });
                      }}
                      className={`group/sub w-full text-left pl-3 pr-2 py-1.5 rounded-md text-[10px] font-medium transition-colors flex items-center justify-between relative
                                ${isModelActive
                          ? "text-sky-700 bg-sky-50"
                          : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                        }
                            `}
                    >
                      {/* Horizontal Branch Line */}
                      <div className={`absolute left-0 top-1/2 w-2 h-px -translate-x-full transition-colors ${isModelActive ? "bg-sky-400" : "bg-slate-200"}`} />

                      <span className={`truncate ${isModelActive ? "font-bold" : ""}`}>{displayName}</span>

                      {model.label && (() => {
                        let labelText = model.label;
                        let labelStyle = "bg-slate-100 text-slate-400 border-slate-200";
                        let labelIconName = null;

                        // Try Parse JSON Label
                        if (typeof labelText === 'string' && (labelText.trim().startsWith('{') || labelText.includes('"text":'))) {
                          try {
                            const parsed = JSON.parse(labelText);
                            labelText = parsed.text || labelText;
                            labelIconName = parsed.icon;

                            // Map colors
                            const color = parsed.color || 'slate';
                            if (color === 'rose' || color === 'red') labelStyle = "bg-rose-50 text-rose-600 border-rose-100";
                            else if (color === 'orange' || color === 'amber') labelStyle = "bg-orange-50 text-orange-600 border-orange-100";
                            else if (color === 'emerald' || color === 'green') labelStyle = "bg-emerald-50 text-emerald-600 border-emerald-100";
                            else if (color === 'blue' || color === 'sky') labelStyle = "bg-sky-50 text-sky-600 border-sky-100";
                            else if (color === 'violet' || color === 'purple') labelStyle = "bg-violet-50 text-violet-600 border-violet-100";

                          } catch (e) {
                            // Fallback regex for text and icon
                            const textMatch = labelText.match(/"text":"([^"]+)"/);
                            if (textMatch) labelText = textMatch[1];
                            const iconMatch = labelText.match(/"icon":"([^"]+)"/);
                            if (iconMatch) labelIconName = iconMatch[1];
                          }
                        }

                        // Render Icon if exists
                        let IconComponent = null;
                        if (labelIconName) {
                          // Case-insensitive lookup
                          const pascalName = labelIconName.charAt(0).toUpperCase() + labelIconName.slice(1).toLowerCase();
                          IconComponent = LucideIcons[pascalName] || LucideIcons[labelIconName];
                        }

                        return (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${labelStyle} group-hover/sub:border-opacity-100 flex items-center gap-1`}>
                            {IconComponent && <IconComponent size={8} className="stroke-[2.5]" />}
                            {labelText}
                          </span>
                        );
                      })()}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-900/30 md:hidden ${isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setIsOpen(false)}
      />

      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          bg-gradient-to-b from-sky-50 via-white to-slate-50
          border-r border-slate-200/80
          flex flex-col min-h-0
          md:static md:translate-x-0 md:h-screen
          transition-all duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${effectiveCollapsed ? "w-full md:w-[72px]" : "w-full md:w-64"}
        `}
      >
        {/* Header */}
        <div className={`h-14 flex items-center border-b border-slate-200/80 flex-shrink-0 relative transition-all duration-300 ${effectiveCollapsed ? 'justify-center px-2' : 'justify-start px-5'}`}>
          {effectiveCollapsed ? (
            // Collapsed: Show only icon from public assets
            <img src="/assets/dashboard/icon.svg" alt="Neo AI" className="w-9 h-9" />
          ) : (
            // Expanded: Show logo and workspace text
            <div className="flex flex-col justify-center items-start">
              <img src={logoNeoAI} alt="Neo AI" className="h-5 w-auto mb-0.5" />
              <span className="text-[8px] text-slate-400 font-medium tracking-widest uppercase ml-0.5">{t('sidebar.workspace')}</span>
            </div>
          )}

          {/* Mobile close button */}
          <button
            type="button"
            className="md:hidden p-1.5 rounded-full hover:bg-slate-100 text-slate-500 absolute right-4 top-1/2 -translate-y-1/2"
            onClick={() => setIsOpen(false)}
          >
            <LucideIcons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 pb-6 space-y-6 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">

          <div className="space-y-1 mt-6">
            {!effectiveCollapsed && (
              <p className="px-7 text-[10px] font-bold text-slate-400/80 uppercase tracking-widest mb-2 font-sans">{t('sidebar.mainMenu')}</p>
            )}
            {fixedItems.map(item => renderItem(item, effectiveCollapsed))}
          </div>

          {userTools.length > 0 && (
            <div className="space-y-1">
              {!effectiveCollapsed && (
                <div className="flex items-center justify-between px-7 mb-2">
                  <p className="text-[10px] font-bold text-slate-400/80 uppercase tracking-widest font-sans">{t('sidebar.myTools')}</p>
                  <span className="bg-sky-100 text-sky-600 text-[9px] px-1.5 py-0.5 rounded font-bold">{userTools.length}</span>
                </div>
              )}
              {userTools.map(item => renderItem(item, effectiveCollapsed))}
            </div>
          )}
        </nav>

        {/* Footer with Logout and Collapse Toggle */}
        <div className={`flex-shrink-0 border-t border-slate-200/80 bg-white/50 backdrop-blur-sm transition-all duration-300 ${effectiveCollapsed ? 'p-2' : 'p-4'}`}>
          {effectiveCollapsed ? (
            // Collapsed: Show icon-only logout
            <div className="flex flex-col items-center gap-2">
              <LogoutButton className="w-10 h-10 justify-center p-0 rounded-lg" iconOnly />
            </div>
          ) : (
            <LogoutButton className="w-full justify-center text-xs py-2.5 font-bold shadow-sm" />
          )}
        </div>

        {/* Collapse Toggle Button - Desktop Only */}
        <button
          type="button"
          onClick={toggleCollapse}
          className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 shadow-sm transition-all z-50"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <LucideIcons.ArrowLeftFromLine className={`w-3.5 h-3.5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>
      </aside>
    </>
  );
};

export default DashboardSidebar;
